import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Drawer,
  Divider,
  Chip,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';

// --- INTERFACES AND CONSTANTS ---
const YOUR_TEXTURE_SVG_PATH = '/textures/Texture.svg';
const YOUR_GRADIENT_SVG_PATH = '/images/Gradient_Popup.svg';
const DEFAULT_PANEL_ICON = '/icons/budget.svg';

interface ScopeConfiguration {
    id: string;
    dataSources: { id: string, name: string }[];
    regions: string[] | null;
    services: string[] | null;
    labels: { [key: string]: string } | null;
    k8sClusters: string[] | null;
    k8sNamespaces: string[] | null;
}

interface Budget {
    id: string;
    name: string;
    description: string;
    period: 'MONTHLY' | 'QUARTERLY' | 'YEARLY';
    budgetLimit: number;
    thresholds: number[];
    tags?: { [key: string]: string } | null;
    provider: string;
    scopeConfigurations: ScopeConfiguration[];
    metric: 'COST' | 'TOKENS';
}

interface BudgetDetailsPanelProps {
  open: boolean;
  onClose: () => void;
  budget: Budget | null;
}

// --- HELPER COMPONENTS ---
const SectionHeader: React.FC<{ icon: string; title: string }> = ({ icon, title }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
    <img src={icon} alt={title} style={{ width: '24px', height: '24px' }} />
    <Typography variant="h6" sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '14px', lineHeight: '20px', color: '#333333', }}>
      {title}
    </Typography>
  </Box>
);

const DetailItem: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box>
    <Typography variant="body2" sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '12px', lineHeight: '18px', color: '#666666', mb: 0.5 }}>
      {label}
    </Typography>
    <Typography variant="body1" sx={{ fontFamily: 'Inter', fontWeight: 500, fontSize: '14px', lineHeight: '20px', color: '#333333', wordBreak: 'break-word' }}>
      {value}
    </Typography>
  </Box>
);

const ScopeItem: React.FC<{ label: string; values: string[] | null | undefined }> = ({ label, values }) => {
    if (!values || values.length === 0) return null;
    return <DetailItem label={label} value={values.join(', ')} />;
};

const LabelChips: React.FC<{ labels: { [key: string]: string } | null | undefined }> = ({ labels }) => {
    if (!labels || Object.keys(labels).length === 0) return null;
    return (
        <Box>
            <Typography variant="body2" sx={{ color: '#666', fontSize: '12px', lineHeight: '18px', fontWeight: 400, mb: 1 }}>
                Labels
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {Object.entries(labels).map(([key, value], index) => (
                    <Chip key={index} label={`${key}: ${value}`} size="small" />
                ))}
            </Box>
        </Box>
    );
}

// --- MAIN COMPONENT ---
const BudgetDetailsPanel: React.FC<BudgetDetailsPanelProps> = ({ open, onClose, budget }) => {
  const navigate = useNavigate();

  const displayBudget: Budget | null = budget ? { 
    ...budget, 
    metric: budget.metric || 'COST',
    description: budget.description || "No description provided.", 
    tags: budget.tags || {}, 
  } : null;

  const handleEditClick = () => {
    if (budget?.id) {
        navigate(`/budget-alerts/edit/${budget.id}`);
    }
  };

  const formatPeriod = (period: string) => {
    if (!period) return 'N/A';
    return period.charAt(0).toUpperCase() + period.slice(1).toLowerCase();
  }

  const formatValue = (value: number, metric?: 'COST' | 'TOKENS' | string) => {
    if (typeof value !== 'number') return '-';
    const formattedNumber = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);

    if (metric === 'TOKENS') {
        return `${formattedNumber} T`;
    }

    return `$${formattedNumber}`;
  }

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { borderRadius: '12px', margin: '20px', height: 'calc(100% - 40px)', width: '450px', boxShadow: '0px 8px 24px rgba(0,0,0,0.15)', overflow: 'hidden', }, }}>
      <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'white', }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, backgroundImage: `url(${YOUR_TEXTURE_SVG_PATH}), url(${YOUR_GRADIENT_SVG_PATH})`, backgroundRepeat: 'no-repeat', backgroundSize: 'cover', backgroundPosition: 'center', p: 1, minHeight: '120px', }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p:2, marginBottom:2 }}>
                  <Box sx={{ width: 56, height: 56, borderRadius: '10px', backgroundColor: '#E5EFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, }}>
                      <img src={DEFAULT_PANEL_ICON} alt="Details" style={{ width: 23, height: 23 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '18px', color: '#333333', }}>
                      Budget Details
                  </Typography>
              </Box>
              <IconButton onClick={onClose} sx={{ color: '#333333', mb:4, mr:2 }}>
                  <CloseIcon />
              </IconButton>
          </Box>
        
        <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3 }}>
            {displayBudget ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <SectionHeader icon="/icons/Basic-Details.svg" title="Basic Details" />
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <DetailItem label="Budget Name" value={displayBudget.name} />
                    <DetailItem label="Budget Description" value={displayBudget.description} />
                  </Box>
                  <Box sx={{ mt: 2 }}>
                     <LabelChips labels={displayBudget.tags} />
                  </Box>
                </Box>
                 <Divider sx={{ mx: -3 }}/>

                {displayBudget.scopeConfigurations.map((scope, index) => (
                    <React.Fragment key={scope.id}>
                        <Box>
                            <SectionHeader icon="/icons/Scope-Config.svg" title={`Scope Configuration ${displayBudget.scopeConfigurations.length > 1 ? index + 1 : ''}`} />
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                <DetailItem label="Data Source Name" value={scope.dataSources?.map(ds => ds.name).join(', ') || "Any"} />
                                <DetailItem label="Provider" value={displayBudget.provider} />
                                <ScopeItem label="Regions" values={scope.regions} />
                                <ScopeItem label="Services" values={scope.services} />
                                <ScopeItem label="K8s Clusters" values={scope.k8sClusters} />
                                <ScopeItem label="K8s Namespaces" values={scope.k8sNamespaces} />
                            </Box>
                             <Box sx={{ mt: 2 }}>
                                 <LabelChips labels={scope.labels} />
                             </Box>
                        </Box>
                        {index < displayBudget.scopeConfigurations.length - 1 && <Divider sx={{ mx: -3 }} />}
                    </React.Fragment>
                ))}

                <Divider sx={{ mx: -3 }}/>

                <Box>
                  <SectionHeader icon="/icons/Budget_Details_1.svg" title="Budget Details" />
                   <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                    <DetailItem label="Budget Period" value={formatPeriod(displayBudget.period)} />
                    <DetailItem 
                      label={displayBudget.metric === 'TOKENS' ? 'Budget Tokens' : 'Budget Amount'} 
                      value={formatValue(displayBudget.budgetLimit, displayBudget.metric)} 
                    />
                  </Box>
                  <Typography variant="body2" sx={{ color: '#666', fontSize: '12px', lineHeight: '18px', fontWeight: 500, mb: 1.5 }}>
                        Alert Thresholds
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {displayBudget.thresholds.map((threshold, index) => (
                        <Box key={index} sx={{ p: 2, bgcolor: '#F9FAFB', borderRadius: '8px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2}}>
                           <DetailItem label="Budget In Percentage" value={`${threshold}%`} />
                           <DetailItem 
                             label={displayBudget.metric === 'TOKENS' ? 'Budget in Tokens' : 'Budget In Amount'} 
                             value={formatValue((displayBudget.budgetLimit * threshold) / 100, displayBudget.metric)} 
                           />
                        </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            ) : (
              <Typography>No budget selected.</Typography>
            )}
        </Box>

        <Box sx={{ p: 2, borderTop: '1px solid #E0E0E0', flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              onClick={handleEditClick}
              variant="contained" 
              disableElevation 
              sx={{ textTransform: 'none', backgroundColor: '#0064FF', width: '110px', height: '36px', borderRadius: '8px', padding: '10px 16px', '&:hover': { backgroundColor: '#0053D9' } }}
            >
                Edit Budget
            </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default BudgetDetailsPanel;


// import React from 'react';
// import {
//   Box,
//   Typography,
//   IconButton,
//   Drawer,
//   Divider,
//   Chip,
//   Button,
// } from '@mui/material';
// import CloseIcon from '@mui/icons-material/Close';
// import { useNavigate } from 'react-router-dom'; // ✨ IMPORT: Added useNavigate

// // --- INTERFACES AND CONSTANTS ---
// const YOUR_TEXTURE_SVG_PATH = '/textures/Texture.svg';
// const YOUR_GRADIENT_SVG_PATH = '/images/Gradient_Popup.svg';
// const DEFAULT_PANEL_ICON = '/icons/budget.svg';

// interface ScopeConfiguration {
//     id: string;
//     dataSources: { id: string, name: string }[];
//     regions: string[] | null;
//     services: string[] | null;
//     labels: { [key: string]: string } | null;
//     k8sClusters: string[] | null;
//     k8sNamespaces: string[] | null;
// }

// interface Budget {
//     id: string;
//     name: string;
//     description: string;
//     period: 'MONTHLY' | 'QUARTERLY' | 'YEARLY';
//     budgetLimit: number;
//     thresholds: number[];
//     tags?: { [key: string]: string } | null;
//     provider: string;
//     scopeConfigurations: ScopeConfiguration[];
// }

// interface BudgetDetailsPanelProps {
//   open: boolean;
//   onClose: () => void;
//   budget: Budget | null;
// }

// // --- HELPER COMPONENTS ---
// const SectionHeader: React.FC<{ icon: string; title: string }> = ({ icon, title }) => (
//   <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
//     <img src={icon} alt={title} style={{ width: '24px', height: '24px' }} />
//     <Typography variant="h6" sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '14px', lineHeight: '20px', color: '#333333', }}>
//       {title}
//     </Typography>
//   </Box>
// );

// const DetailItem: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
//   <Box>
//     <Typography variant="body2" sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '12px', lineHeight: '18px', color: '#666666', mb: 0.5 }}>
//       {label}
//     </Typography>
//     <Typography variant="body1" sx={{ fontFamily: 'Inter', fontWeight: 500, fontSize: '14px', lineHeight: '20px', color: '#333333', wordBreak: 'break-word' }}>
//       {value}
//     </Typography>
//   </Box>
// );

// const ScopeItem: React.FC<{ label: string; values: string[] | null | undefined }> = ({ label, values }) => {
//     if (!values || values.length === 0) return null;
//     return <DetailItem label={label} value={values.join(', ')} />;
// };

// const LabelChips: React.FC<{ labels: { [key: string]: string } | null | undefined }> = ({ labels }) => {
//     if (!labels || Object.keys(labels).length === 0) return null;
//     return (
//         <Box>
//             <Typography variant="body2" sx={{ color: '#666', fontSize: '12px', lineHeight: '18px', fontWeight: 400, mb: 1 }}>
//                 Labels
//             </Typography>
//             <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
//                 {Object.entries(labels).map(([key, value], index) => (
//                     <Chip key={index} label={`${key}: ${value}`} size="small" />
//                 ))}
//             </Box>
//         </Box>
//     );
// }

// // --- MAIN COMPONENT ---
// const BudgetDetailsPanel: React.FC<BudgetDetailsPanelProps> = ({ open, onClose, budget }) => {
//   const navigate = useNavigate(); // ✨ ADDED: Initialize the navigate function

//   const displayBudget: Budget | null = budget ? { ...budget, description: budget.description || "No description provided.", tags: budget.tags || {}, } : null;

//   const handleEditClick = () => {
//     if (budget?.id) {
//         navigate(`/budget-alerts/edit/${budget.id}`);
//     }
//   };

//   const formatPeriod = (period: string) => {
//     if (!period) return 'N/A';
//     return period.charAt(0).toUpperCase() + period.slice(1).toLowerCase();
//   }

//   const formatCurrency = (value: number) => {
//     if (typeof value !== 'number') return '-';
//      return new Intl.NumberFormat('en-US', {
//         style: 'currency',
//         currency: 'USD',
//         minimumFractionDigits: 0,
//         maximumFractionDigits: 0,
//     }).format(value);
//   }

//   return (
//     <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { borderRadius: '12px', margin: '20px', height: 'calc(100% - 40px)', width: '450px', boxShadow: '0px 8px 24px rgba(0,0,0,0.15)', overflow: 'hidden', }, }}>
//       <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'white', }}>
//           <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, backgroundImage: `url(${YOUR_TEXTURE_SVG_PATH}), url(${YOUR_GRADIENT_SVG_PATH})`, backgroundRepeat: 'no-repeat', backgroundSize: 'cover', backgroundPosition: 'center', p: 1, minHeight: '120px', }}>
//               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p:2, marginBottom:2 }}>
//                   <Box sx={{ width: 56, height: 56, borderRadius: '10px', backgroundColor: '#E5EFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, }}>
//                       <img src={DEFAULT_PANEL_ICON} alt="Details" style={{ width: 23, height: 23 }} />
//                   </Box>
//                   <Typography variant="h6" sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '18px', color: '#333333', }}>
//                       Budget Details
//                   </Typography>
//               </Box>
//               <IconButton onClick={onClose} sx={{ color: '#333333', mb:4, mr:2 }}>
//                   <CloseIcon />
//               </IconButton>
//           </Box>
        
//         <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3 }}>
//             {displayBudget ? (
//               <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
//                 <Box>
//                   <SectionHeader icon="/icons/Basic-Details.svg" title="Basic Details" />
//                   <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
//                     <DetailItem label="Budget Name" value={displayBudget.name} />
//                     <DetailItem label="Budget Description" value={displayBudget.description} />
//                   </Box>
//                   <Box sx={{ mt: 2 }}>
//                      <LabelChips labels={displayBudget.tags} />
//                   </Box>
//                 </Box>
//                  <Divider sx={{ mx: -3 }}/>

//                 {displayBudget.scopeConfigurations.map((scope, index) => (
//                     <React.Fragment key={scope.id}>
//                         <Box>
//                             <SectionHeader icon="/icons/Scope-Config.svg" title={`Scope Configuration ${displayBudget.scopeConfigurations.length > 1 ? index + 1 : ''}`} />
//                             <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
//                                 <DetailItem label="Data Source Name" value={scope.dataSources?.map(ds => ds.name).join(', ') || "Any"} />
//                                 <DetailItem label="Provider" value={displayBudget.provider} />
//                                 <ScopeItem label="Regions" values={scope.regions} />
//                                 <ScopeItem label="Services" values={scope.services} />
//                                 <ScopeItem label="K8s Clusters" values={scope.k8sClusters} />
//                                 <ScopeItem label="K8s Namespaces" values={scope.k8sNamespaces} />
//                             </Box>
//                              <Box sx={{ mt: 2 }}>
//                                  <LabelChips labels={scope.labels} />
//                              </Box>
//                         </Box>
//                         {index < displayBudget.scopeConfigurations.length - 1 && <Divider sx={{ mx: -3 }} />}
//                     </React.Fragment>
//                 ))}

//                 <Divider sx={{ mx: -3 }}/>

//                 <Box>
//                   <SectionHeader icon="/icons/Budget_Details_1.svg" title="Budget Details" />
//                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
//                     <DetailItem label="Budget Period" value={formatPeriod(displayBudget.period)} />
//                     <DetailItem label="Budget Amount" value={formatCurrency(displayBudget.budgetLimit)} />
//                   </Box>
//                   <Typography variant="body2" sx={{ color: '#666', fontSize: '12px', lineHeight: '18px', fontWeight: 500, mb: 1.5 }}>
//                         Alert Thresholds
//                   </Typography>
//                   <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
//                     {displayBudget.thresholds.map((threshold, index) => (
//                         <Box key={index} sx={{ p: 2, bgcolor: '#F9FAFB', borderRadius: '8px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2}}>
//                            <DetailItem label="Budget In Percentage" value={`${threshold}%`} />
//                            <DetailItem label="Budget In Amount" value={formatCurrency((displayBudget.budgetLimit * threshold) / 100)} />
//                         </Box>
//                     ))}
//                   </Box>
//                 </Box>
//               </Box>
//             ) : (
//               <Typography>No budget selected.</Typography>
//             )}
//         </Box>

//         <Box sx={{ p: 2, borderTop: '1px solid #E0E0E0', flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
//             {/* ✨ UPDATED: Added onClick handler to the button */}
//             <Button 
//               onClick={handleEditClick}
//               variant="contained" 
//               disableElevation 
//               sx={{ textTransform: 'none', backgroundColor: '#0064FF', width: '110px', height: '36px', borderRadius: '8px', padding: '10px 16px', '&:hover': { backgroundColor: '#0053D9' } }}
//             >
//                 Edit Budget
//             </Button>
//         </Box>
//       </Box>
//     </Drawer>
//   );
// };

// export default BudgetDetailsPanel;